import React, { useMemo, useState } from "react";

const INITIAL_FORM = {
  name: "",
  email: "",
  rating: "",
  comment: "",
};

const GENERIC_SUBMIT_ERROR =
  "No pudimos registrar tu opinión. Revisá los datos e intentá de nuevo.";
const GENERIC_SUCCESS_MESSAGE =
  "Recibimos tu opinión correctamente y la procesaremos según el resultado de moderación.";

function normalizeName(value) {
  return String(value || "")
    .replace(/\r\n?/g, "\n")
    .replace(/\n+/g, " ")
    .replace(/[ \t]+/g, " ")
    .trim();
}

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function normalizeComment(value) {
  return String(value || "")
    .replace(/\r\n?/g, "\n")
    .trim()
    .replace(/\n{3,}/g, "\n\n");
}

function hasObviousMarkup(value) {
  if (!value) {
    return false;
  }

  return (
    /<[^>]+>/.test(value) ||
    /(?:&lt;|&gt;|&#x0*3c;|&#x0*3e;|&#0*60;|&#0*62;|%3c|%3e)/i.test(value) ||
    value.includes("<") ||
    value.includes(">")
  );
}

function buildValidationErrors(formState) {
  const errors = {};
  const normalizedName = normalizeName(formState.name);
  const normalizedEmail = normalizeEmail(formState.email);
  const normalizedComment = normalizeComment(formState.comment);
  const rating = Number.parseInt(formState.rating, 10);

  if (!normalizedName) {
    errors.name = "Ingresá tu nombre.";
  }

  if (!normalizedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
    errors.email = "Ingresá un email válido.";
  }

  if (!Number.isInteger(rating) || rating < 1 || rating > 5) {
    errors.rating = "Elegí una calificación del 1 al 5.";
  }

  if (!normalizedComment) {
    errors.comment = "Escribí tu opinión antes de enviarla.";
  } else if (hasObviousMarkup(normalizedComment)) {
    errors.comment = "Escribí tu opinión en texto plano.";
  }

  return {
    errors,
    normalized: {
      name: normalizedName,
      email: normalizedEmail,
      rating,
      comment: normalizedComment,
    },
  };
}

export default function OpinionForm({ entityType, entityId, entityPath, entityLabel }) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const heading = useMemo(
    () => `Compartí tu opinión sobre ${entityLabel}`,
    [entityLabel]
  );

  const handleFieldChange = (fieldName) => (event) => {
    const nextValue = event.target.value;

    setForm((current) => ({
      ...current,
      [fieldName]: nextValue,
    }));

    setErrors((current) => {
      if (!current[fieldName]) {
        return current;
      }

      const nextErrors = { ...current };
      delete nextErrors[fieldName];
      return nextErrors;
    });

    if (submitError) {
      setSubmitError("");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validation = buildValidationErrors(form);
    if (Object.keys(validation.errors).length > 0) {
      setErrors(validation.errors);
      setSubmitError("");
      return;
    }

    setErrors({});
    setSubmitError("");
    setSuccessMessage("");
    setIsSubmitting(true);

    try {
      const response = await fetch("/submit_opinion.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          entityType,
          entityId,
          entityPath,
          entityLabel,
          name: validation.normalized.name,
          email: validation.normalized.email,
          rating: validation.normalized.rating,
          comment: validation.normalized.comment,
        }),
      });

      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error("opinion-submit-failed");
      }

      setForm(INITIAL_FORM);
      setSuccessMessage(payload?.data?.message || payload?.message || GENERIC_SUCCESS_MESSAGE);
      setIsSubmitted(true);
    } catch (error) {
      setSubmitError(GENERIC_SUBMIT_ERROR);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <section className="panel opinion-thank-you" aria-live="polite">
        <span className="eyebrow">Opiniones</span>
        <h2>Gracias por compartir tu opinión</h2>
        <p>{successMessage || GENERIC_SUCCESS_MESSAGE}</p>
      </section>
    );
  }

  return (
    <section className="panel opinion-panel">
      <div className="opinion-panel-copy">
        <span className="eyebrow">Opiniones</span>
        <h2>{heading}</h2>
        <p>
          Completá este formulario inline para dejar una referencia útil sobre esta página.
          Publicamos solo texto plano y nunca mostramos tu email.
        </p>
      </div>

      <form className="opinion-form" onSubmit={handleSubmit} noValidate>
        <div className="opinion-form-grid">
          <label className="field" htmlFor="opinion-name">
            <span>Nombre</span>
            <input
              id="opinion-name"
              type="text"
              value={form.name}
              onChange={handleFieldChange("name")}
              aria-invalid={errors.name ? "true" : "false"}
              aria-describedby={errors.name ? "opinion-name-error" : undefined}
            />
            {errors.name ? (
              <small className="field-error" id="opinion-name-error">
                {errors.name}
              </small>
            ) : null}
          </label>

          <label className="field" htmlFor="opinion-email">
            <span>Email</span>
            <input
              id="opinion-email"
              type="email"
              value={form.email}
              onChange={handleFieldChange("email")}
              aria-invalid={errors.email ? "true" : "false"}
              aria-describedby={errors.email ? "opinion-email-error" : undefined}
            />
            {errors.email ? (
              <small className="field-error" id="opinion-email-error">
                {errors.email}
              </small>
            ) : null}
          </label>

          <label className="field" htmlFor="opinion-rating">
            <span>Calificación</span>
            <select
              id="opinion-rating"
              value={form.rating}
              onChange={handleFieldChange("rating")}
              aria-invalid={errors.rating ? "true" : "false"}
              aria-describedby={errors.rating ? "opinion-rating-error" : undefined}
            >
              <option value="">Elegí una nota</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5</option>
            </select>
            {errors.rating ? (
              <small className="field-error" id="opinion-rating-error">
                {errors.rating}
              </small>
            ) : null}
          </label>
        </div>

        <label className="field" htmlFor="opinion-comment">
          <span>Comentario</span>
          <textarea
            id="opinion-comment"
            rows="5"
            value={form.comment}
            onChange={handleFieldChange("comment")}
            aria-invalid={errors.comment ? "true" : "false"}
            aria-describedby={errors.comment ? "opinion-comment-error" : undefined}
          />
          {errors.comment ? (
            <small className="field-error" id="opinion-comment-error">
              {errors.comment}
            </small>
          ) : null}
        </label>

        {submitError ? (
          <p className="form-error" role="alert">
            {submitError}
          </p>
        ) : null}

        <button className="button button-primary" type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Enviando..." : "Enviar opinión"}
        </button>
      </form>
    </section>
  );
}
